import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Trash2, MapPin, CalendarDays, Link2, QrCode, X } from "lucide-react";
import QRCode from "qrcode";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function EventsDialog({ open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ name: "", venue: "", event_date: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [qrEvent, setQrEvent] = useState(null);
  const [qrUrl, setQrUrl] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/events", form);
      toast.success(`Event "${form.name}" added`);
      setForm({ name: "", venue: "", event_date: "" });
      load();
      onChanged?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const del = async (ev) => {
    if (!ev.id) {
      toast.error("This event only exists on patient records — edit those patients to clear it first");
      return;
    }
    try {
      const { data } = await api.delete(`/events/${ev.id}`);
      toast.success(data.message);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyLiveLink = async (ev) => {
    const link = `${window.location.origin}/live/${ev.share_token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Live view link copied — send it to the event organiser");
    } catch (e) {
      toast.info(link);
    }
  };

  const showQr = async (ev) => {
    const link = `${window.location.origin}/checkin/${ev.checkin_token}`;
    try {
      setQrUrl(await QRCode.toDataURL(link, { width: 640, margin: 2 }));
    } catch (e) {
      setQrUrl("");
    }
    setQrEvent({ ...ev, link });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="events-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#0F2547] flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-[#005EB8]" /> Manage Events
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Add events you're covering. Deleting an event moves its patients to Unassigned.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={add} className="rounded-xl border border-slate-200 bg-[#F4F7FA] p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="add-event-form">
          <input required placeholder="Event name *" value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="event-name-input"
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
          <input placeholder="Venue" value={form.venue}
            onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))}
            data-testid="event-venue-input"
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
          <input type="date" title="Event date" value={form.event_date}
            onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
            data-testid="event-date-input"
            className="h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
          {error && (
            <p className="sm:col-span-3 text-xs font-semibold text-[#D5281B]" data-testid="event-form-error">{error}</p>
          )}
          <button type="submit" disabled={saving} data-testid="add-event-submit"
            className="sm:col-span-3 h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60">
            {saving ? "Adding…" : "Add event"}
          </button>
        </form>

        <div className="space-y-2">
          {events.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4" data-testid="no-events-message">No events yet — add your first one above.</p>
          )}
          {events.map((ev) => (
            <div key={ev.id || ev.name} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`event-row-${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0F2547] truncate">{ev.name}</p>
                <p className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                  {ev.venue && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{ev.venue}</span>}
                  {ev.event_date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{ev.event_date}</span>}
                </p>
              </div>
              {ev.checkin_token && (
                <button onClick={() => showQr(ev)} data-testid={`checkin-qr-btn-${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-[#005EB8] hover:bg-sky-50 rounded-lg p-2 transition-colors min-h-[36px] min-w-[36px] grid place-items-center"
                  title="Patient self check-in QR code">
                  <QrCode className="h-4 w-4" />
                </button>
              )}
              {ev.share_token && (
                <button onClick={() => copyLiveLink(ev)} data-testid={`copy-live-link-${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-[#005EB8] hover:bg-sky-50 rounded-lg p-2 transition-colors min-h-[36px] min-w-[36px] grid place-items-center"
                  title="Copy live organiser view link">
                  <Link2 className="h-4 w-4" />
                </button>
              )}
              {user?.role === "admin" && (
                <button onClick={() => del(ev)} data-testid={`delete-event-btn-${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-[#D5281B] hover:bg-red-50 rounded-lg p-2 transition-colors min-h-[36px] min-w-[36px] grid place-items-center"
                  title="Delete event (admin only)">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {qrEvent && (
          <div className="mt-4 rounded-xl border border-[#005EB8]/30 bg-sky-50 p-4 text-center" data-testid="checkin-qr-panel">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold text-[#0F2547]">Patient self check-in — {qrEvent.name}</p>
              <button onClick={() => setQrEvent(null)} data-testid="checkin-qr-close"
                className="p-1.5 text-slate-400 hover:text-[#D5281B] transition-colors min-h-[36px] min-w-[36px] grid place-items-center">
                <X className="h-4 w-4" />
              </button>
            </div>
            {qrUrl && (
              <img src={qrUrl} alt={`Check-in QR for ${qrEvent.name}`} data-testid="checkin-qr-image"
                className="mx-auto w-56 h-56 rounded-lg border border-slate-200 bg-white p-2" />
            )}
            <p className="text-[11px] text-slate-500 mt-2 break-all">{qrEvent.link}</p>
            <button
              onClick={async () => {
                try { await navigator.clipboard.writeText(qrEvent.link); toast.success("Check-in link copied"); }
                catch { toast.info(qrEvent.link); }
              }}
              data-testid="checkin-copy-link"
              className="mt-1 text-xs font-bold text-[#005EB8] hover:underline min-h-[36px]">
              Copy check-in link
            </button>
            <p className="text-[11px] text-slate-400 mt-1">Show this on a screen or print it — patients scan to check themselves in.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
